import { z } from 'zod';
import { AERIE_MODES, ALLY_ROLES, STATION_KINDS } from '../data/shefira.js';
/**
 * Shefira's Aire (ADR 0068). Intents are what a player asks: to go in at
 * the crevasse, to use one of the Company's posts. Events are what the
 * room decided, for the client to draw and sound; it never answers them.
 */
const TileSchema = z.object({ x: z.number().int(), y: z.number().int() });
const EntityIdSchema = z.string().min(1).max(64);
const NameSchema = z.string().min(1).max(40);
// ——— Intents (client → server) ———
/**
 * At the crevasse, in the world: `story` alone while the Company's last
 * stand is under way; `raid` from a party's leader (or an alliance's), for
 * everyone gathered there. The server checks everything again.
 */
export const AerieEnterMessageSchema = z.object({ mode: z.enum(AERIE_MODES) }).strict();
/** At the crevasse: what may be done now, answered by `aerie_gate`. */
export const AerieStatusRequestSchema = z.object({}).strict();
/** A post of the Company by the arena (ballista, frost brazier, horn), from next to it. */
export const UseStationMessageSchema = z
    .object({ stationId: z.string().regex(/^[a-z][a-z-]{0,31}$/) })
    .strict();
// ——— Events (server → client) ———
/** Why the crevasse stays shut, to the one who asked; `name` is the raider who holds it back. */
export const AerieRejectedSchema = z
    .object({
    reason: z.enum([
        'invalid',
        'no-gate',
        'too-far',
        'dead',
        'in-combat',
        'no-quest',
        'not-leader',
        'too-few',
        'too-many',
        'level',
        'not-stood',
        'absent',
        'busy',
    ]),
    name: NameSchema.optional(),
})
    .strict();
/**
 * Answer to `aerie_status`: whether the story is open to this player, and
 * the raid as their party (and its ally) stands, member by member.
 */
export const AerieGateEventSchema = z
    .object({
    story: z.boolean(),
    raid: z.object({
        /** This player may launch it: they lead a party or an alliance. */
        leader: z.boolean(),
        members: z
            .array(z.object({
            name: NameSchema,
            level: z.number().int().min(1),
            stood: z.boolean(),
            present: z.boolean(),
        }))
            .max(10),
        /** Null when it may go in now. */
        refusal: AerieRejectedSchema.shape.reason.nullable(),
    }),
})
    .strict();
export const SHEFIRA_EVENT_PHASES = ['ground', 'flight', 'enraged'];
/**
 * The fight as it stands, to every player of the instance: at their join
 * and whenever it changes. `airborne` is when she may not be struck.
 */
export const ShefiraStateEventSchema = z
    .object({
    mobId: EntityIdSchema,
    mode: z.enum(AERIE_MODES),
    /** How many the fight was sized for (1 in the story). */
    players: z.number().int().min(1).max(10),
    phase: z.enum(SHEFIRA_EVENT_PHASES),
    airborne: z.boolean(),
    /** While she flies: the longest she stays up from now, in ms. */
    landsInMs: z.number().int().min(0).nullable(),
    /** The ballistae whose harpoon is in her this flight. */
    harpoons: z.array(z.string().min(1)).max(8),
    /** Phase 3: the rings round her the next ring of fire spares; null before. */
    safeRadius: z.number().int().min(0).nullable(),
})
    .strict();
/**
 * She rises (`takeoff`, clip `takeoff` then `hover` and her shadow) or comes
 * down (`landing`): `ms` until she is out of reach, or back in it.
 * `cause` says why she lands: pinned by two harpoons, or tired of the sky.
 */
export const ShefiraFlightEventSchema = z
    .object({
    mobId: EntityIdSchema,
    state: z.enum(['takeoff', 'landing']),
    ms: z.number().int().min(0),
    cause: z.enum(['harpooned', 'tired']).optional(),
})
    .strict();
/**
 * A dive is marked on a player (her shadow closes on them): the square is
 * also sent as a `boss_telegraph` with its tiles, like every shape.
 * `heralded` when the horn told it earlier.
 */
export const ShefiraDiveEventSchema = z
    .object({
    mobId: EntityIdSchema,
    /** The session id of the player she dives on. */
    target: z.string().min(1),
    centre: TileSchema,
    radius: z.number().int().min(0),
    ms: z.number().int().positive(),
    heralded: z.boolean(),
})
    .strict();
/**
 * Phase 3, the ring of fire: every tile further than `safe` rings from
 * `centre` burns when `ms` runs out (`marked`), then burns (`landed`).
 * Too many tiles for a list: the client draws the ring from `safe` out.
 */
export const ShefiraRingEventSchema = z
    .object({
    mobId: EntityIdSchema,
    stage: z.enum(['marked', 'landed']),
    centre: TileSchema,
    safe: z.number().int().min(0),
    ms: z.number().int().min(0),
})
    .strict();
/** One of the Company's posts, as the arena shows it; `readyInMs` 0 when it may be used. */
export const StationStateSchema = z
    .object({
    id: z.string().min(1),
    kind: z.enum(STATION_KINDS),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    readyInMs: z.number().int().min(0),
})
    .strict();
/** Every post of the arena, at join and after each use. */
export const AerieStationsEventSchema = z
    .object({ stations: z.array(StationStateSchema).max(12) })
    .strict();
/**
 * A post was used, to everyone: who used which, a player (`by`) or, in the
 * story, one of the Company (`ally`, its mob id). For a brazier, the session
 * ids it veiled in frost; for a ballista, whether the harpoon pinned her.
 */
export const StationUsedEventSchema = z
    .object({
    stationId: z.string().min(1),
    kind: z.enum(STATION_KINDS),
    /** The user's session id, when a player used it. */
    by: z.string().min(1).optional(),
    /** The ally's mob id, when one of the Company worked it (ADR 0068 §4). */
    ally: EntityIdSchema.optional(),
    guarded: z.array(z.string().min(1)).max(10).optional(),
    pinned: z.boolean().optional(),
})
    .strict()
    .refine((used) => (used.by === undefined) !== (used.ally === undefined), 'a player or an ally');
export const StationRejectedSchema = z
    .object({
    stationId: z.string().min(1),
    reason: z.enum(['invalid', 'unknown', 'too-far', 'cooldown', 'not-now', 'dead']),
})
    .strict();
/**
 * The story's allies (ADR 0068 §4): one says its last words, then falls a
 * little later (`fell`), is thrown out of the fight alive and limps off
 * (`out`, Ulfar), or plants the sword (`sword`, Ser Givrecoeur, who dies of
 * his burns). `role` is what it held. The fall itself is the mob's death.
 */
export const AerieAllyEventSchema = z
    .object({
    mobId: EntityIdSchema,
    name: NameSchema,
    role: z.enum(ALLY_ROLES),
    event: z.enum(['fell', 'out', 'sword']),
    line: z.string().min(1).max(200),
})
    .strict();
/**
 * One of the story's allies did its part, to everyone, for the client to
 * mark: Méret seals the player against a shape she marked (`ward`, `target`
 * a session id), Mélisande roots a whelp (`roots`, `target` a mob id). The
 * posts the Company works are told by `station_used`.
 */
export const AerieAllyActEventSchema = z
    .object({
    mobId: EntityIdSchema,
    act: z.enum(['ward', 'roots']),
    target: EntityIdSchema,
})
    .strict();
/**
 * The fight is over, to everyone: `victory` (slain in a raid), `fled` (the
 * story won: she flees with the sword in her flank), `defeat` (the story's
 * player fell, or every raider at once). Everyone walks out after `exitInMs`.
 */
export const AerieEndedEventSchema = z
    .object({
    outcome: z.enum(['victory', 'fled', 'defeat']),
    exitInMs: z.number().int().min(0),
    /** Victory only: this player's weekly loot was already had (ADR 0068 §5). */
    lootTaken: z.boolean().optional(),
})
    .strict();
//# sourceMappingURL=shefira.js.map